import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Customer, Contract, PricingHeader } from '../types';
import Modal from '../components/Modal';
import CustomerForm from '../components/CustomerForm';
import StripeCustomerIdEditor from '../components/StripeCustomerIdEditor';

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);

  const fetchCustomerDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCustomer(parseInt(id!));
      setCustomer(response);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchCustomerContracts = useCallback(async () => {
    try {
      setContractsLoading(true);
      // Get contracts for this customer
      const response = await apiService.getContracts(1, 100, undefined, parseInt(id!));
      setContracts(response.items || []);
    } catch (error) {
      console.error('Error fetching customer contracts:', error);
      setContracts([]);
    } finally {
      setContractsLoading(false);
    }
  }, [id]);

  const fetchPricingHeaders = useCallback(async () => {
    try {
      const response = await apiService.getPricingHeaders(0, 100);
      setPricingHeaders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching pricing headers:', error);
      setPricingHeaders([]);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchCustomerDetails();
      fetchCustomerContracts();
      fetchPricingHeaders();
    }
  }, [id, fetchCustomerDetails, fetchCustomerContracts, fetchPricingHeaders]);

  
  
  
  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSubmit = async (data: any) => {
    try {
      if (customer) {
        await apiService.updateCustomer(customer.id, data);
        setShowEditModal(false);
        fetchCustomerDetails();
      }
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  const handleCreateContract = () => {
    navigate('/contracts', { 
      state: { 
        createForCustomer: customer,
        customerId: customer?.id 
      } 
    });
  };

  const getPricingHeaderName = (id: number) => {
    const header = pricingHeaders.find(h => h.pricing_header_id === id);
    return header?.name || `Header ${id}`;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getContractStatusBadge = (contract: Contract) => {
    if (!contract.isactive || contract.isdeleted) {
      return <span className="badge badge-danger">Deleted</span>;
    }
    
    // Check if contract is expired (end_date has passed)
    if (contract.end_date && new Date(contract.end_date) < new Date()) {
      return <span className="badge badge-warning">Expired</span>;
    }
    
    // Check if contract is inactive (start_date equals end_date)
    if (contract.start_date === contract.end_date) {
      return <span className="badge badge-secondary">Inactive</span>;
    }
    
    return <span className="badge badge-success">Active</span>;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="page-header">
        <h1>Customer not found</h1>
        <Link to="/customers" className="btn btn-secondary">
          Back to Customers
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{customer.customer_name}</h1>
          <p style={{ color: '#7f8c8d', margin: '5px 0 0 0' }}>
            Customer ID: {customer.id} • Type: {customer.customer_type}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={handleCreateContract}>
            + Create Contract
          </button>
          <button className="btn btn-secondary" onClick={handleEdit}>
            Edit Customer
          </button>
          <Link to="/customers" className="btn btn-secondary">
            Back to Customers
          </Link>
        </div>
      </div>

      {/* Customer Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Customer Information</h3>
          <span className={`badge ${customer.is_active ? 'badge-success' : 'badge-danger'}`}>
            {customer.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <strong>Contact Name:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_contact_name || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Contact Role:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_contact_role || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Email:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_email_address || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Phone:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_telephone_number || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Address:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {[
                customer.customer_address_street,
                customer.customer_address_city,
                customer.customer_address_postcode,
                customer.customer_address_country
              ].filter(Boolean).join(', ') || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Tax ID:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_tax_id || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>VAT Number:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.customer_vat_number || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Currency:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.currency}
            </p>
          </div>
          <div>
            <strong>Language:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.language?.toUpperCase() || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Payment Status:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {customer.payment_status}
            </p>
          </div>
          <div>
            <strong>Total Amount:</strong>
            <p style={{ margin: '5px 0', color: '#3498db', fontWeight: 'bold' }}>
              {formatCurrency(customer.total_amount, customer.currency)}
            </p>
          </div>
          <div>
            <strong>Created:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {new Date(customer.created_at).toLocaleDateString()}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <strong>Stripe Customer ID:</strong>
              <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
                {customer.stripe_customer_id || 'Not assigned'}
              </p>
            </div>
            <StripeCustomerIdEditor
              customerId={customer.id}
              currentStripeCustomerId={customer.stripe_customer_id || null}
              customerEmail={customer.customer_email_address}
              customerName={customer.customer_name}
              customerAddress={customer.customer_address_street}
              customerVatId={customer.customer_vat_number}
              companyId={contracts.length > 0 ? contracts[0].company_id : undefined}
              onUpdated={(newId) => {
                // Refresh customer data after Stripe ID is updated
                fetchCustomerDetails();
              }}
            />
          </div>
        </div>
        
        {customer.notes && (
          <div style={{ marginTop: '20px' }}>
            <strong>Notes:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d', whiteSpace: 'pre-wrap' }}>
              {customer.notes}
            </p>
          </div>
        )}
      </div>

      {/* Contracts */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Contracts ({contracts.length})</h3>
          <button className="btn btn-primary btn-sm" onClick={handleCreateContract}>
            + Create Contract
          </button>
        </div>
        
        {contractsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
          </div>
        ) : contracts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h4 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Contracts</h4>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              This customer doesn't have any contracts yet.
            </p>
            <button className="btn btn-primary" onClick={handleCreateContract}>
              Create First Contract
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Name</th>
                  <th>Pricing Header</th>
                  <th>Currency</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.contract_id}>
                    <td>{contract.contract_number}</td>
                    <td>
                      <Link 
                        to={`/contracts/${contract.contract_id}`} 
                        style={{ textDecoration: 'none', color: '#3498db', fontWeight: '500' }}
                      >
                        {contract.contract_name}
                      </Link>
                    </td>
                    <td>{getPricingHeaderName(contract.fixed_pricing_header_id)}</td>
                    <td>
                      <span className="badge badge-info">
                        {contract.currency}
                      </span>
                    </td>
                    <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                    <td>{contract.end_date ? new Date(contract.end_date).toLocaleDateString() : '-'}</td>
                    <td>{getContractStatusBadge(contract)}</td>
                    <td>
                      <Link
                        to={`/contracts/${contract.contract_id}`}
                        className="btn btn-outline btn-sm"
                        style={{ marginRight: '5px' }}
                      >
                        View Details
                      </Link>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate('/invoices', { 
                          state: { 
                            contractId: contract.contract_id, 
                            contractName: contract.contract_name 
                          } 
                        })}
                      >
                        Create Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditModal && (
        <Modal
          title="Edit Customer"
          onClose={() => setShowEditModal(false)}
        >
          <CustomerForm
            initialData={customer}
            onSubmit={handleSubmit}
            onCancel={() => setShowEditModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default CustomerDetails;